import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RefreshCw, AlertCircle, ShieldX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDashboardFiltersStore } from '@/store/dashboardFiltersStore';
import type {
  RejectionCategory,
  RejectionReasonEntry,
} from '../api/dashboard.schemas';

interface RejectionReasonsData {
  data: RejectionReasonEntry[];
  totalRejections: number;
}

interface RejectionReasonsChartProps {
  data?: RejectionReasonsData;
  loading?: boolean;
  error?: Error;
  height?: number;
}

/**
 * Per-category styling. The colors deliberately escalate by "graveness":
 *   integrity (deepest red) → spoofing → biometric → document → other (neutral).
 * Operators can spot a sudden bloom of `device_integrity` rejections from
 * across the room.
 *
 * Adding a new category? Add it here AND in `RejectionCategorySchema`
 * (frontend) AND in `REJECTION_CATEGORIES` (backend, rejectionCategorizer.ts).
 * Missing any of those three causes a zod parse error at runtime.
 */
const CATEGORY_STYLE: Record<RejectionCategory, { dot: string; i18nKey: string }> = {
  device_rate_limit: { dot: 'bg-rose-700', i18nKey: 'dashboard.rejectionReasons.deviceRateLimit' },
  device_integrity: { dot: 'bg-red-600', i18nKey: 'dashboard.rejectionReasons.deviceIntegrity' },
  liveness_spoofing: { dot: 'bg-orange-500', i18nKey: 'dashboard.rejectionReasons.livenessSpoofing' },
  biometric_mismatch: { dot: 'bg-amber-500', i18nKey: 'dashboard.rejectionReasons.biometricMismatch' },
  document_invalid: { dot: 'bg-blue-500', i18nKey: 'dashboard.rejectionReasons.documentInvalid' },
  other: { dot: 'bg-gray-400', i18nKey: 'dashboard.rejectionReasons.other' },
};

export function RejectionReasonsChart({
  data,
  loading = false,
  error,
  height = 300,
}: RejectionReasonsChartProps) {
  const { t } = useTranslation();
  const activeCategory = useDashboardFiltersStore((s) => s.filters.rejectionCategory);
  const toggleFilter = useDashboardFiltersStore((s) => s.toggleFilter);

  const header = (description: string) => (
    <CardHeader className="flex-shrink-0">
      <div className="flex items-center gap-2">
        <ShieldX className="h-5 w-5 text-primary" />
        <CardTitle className="text-base">
          {t('dashboard.rejectionReasons.title', 'Rejection reasons')}
        </CardTitle>
      </div>
      <CardDescription>{description}</CardDescription>
    </CardHeader>
  );

  if (loading) {
    return (
      <Card className="h-full flex flex-col">
        {header(t('dashboard.rejectionReasons.subtitle', 'Composition of rejected verifications'))}
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground" style={{ minHeight: height }}>
            <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
            {t('dashboard.loadingChart')}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-full flex flex-col">
        {header(t('dashboard.rejectionReasons.errorSubtitle', 'Failed to load rejection data'))}
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4" style={{ minHeight: height }}>
            <div className="mx-auto w-12 h-12 bg-gradient-to-br from-destructive/10 to-destructive/5 rounded-xl flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive/60" />
            </div>
            <p className="text-sm text-muted-foreground">{error.message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const total = data?.totalRejections ?? 0;
  const entries = data?.data ?? [];

  if (total === 0) {
    return (
      <Card className="h-full flex flex-col">
        {header(t('dashboard.rejectionReasons.subtitle', 'Composition of rejected verifications'))}
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground" style={{ minHeight: height }}>
            <ShieldX className="h-6 w-6 mx-auto mb-2 opacity-50" />
            <p>{t('dashboard.rejectionReasons.empty', 'No rejections in this period')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      {header(
        t('dashboard.rejectionReasons.subtitleWithTotal', {
          defaultValue: 'Composition of {{count}} rejected verifications',
          count: total,
        }),
      )}
      <CardContent className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 space-y-3 overflow-y-auto">
          {entries.map((entry) => {
            const style = CATEGORY_STYLE[entry.category];
            const label = t(style.i18nKey, defaultLabel(entry.category));
            const isActive = activeCategory === entry.category;
            return (
              <button
                type="button"
                key={entry.category}
                onClick={() => toggleFilter('rejectionCategory', entry.category)}
                className={`w-full text-left flex items-center justify-between p-3 rounded-lg border transition-colors hover:bg-muted/40 ${
                  isActive ? 'border-primary ring-1 ring-primary/40 bg-primary/5' : ''
                }`}
                aria-pressed={isActive}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${style.dot}`} />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{label}</p>
                    <p className="text-xs text-muted-foreground">
                      {entry.percentage.toFixed(1)}% {t('dashboard.ofRejections', 'of rejections')}
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <p className="font-semibold">{entry.count}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('dashboard.rejections', 'rejections')}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/** Fallback English label used when i18n key is missing. */
function defaultLabel(c: RejectionCategory): string {
  switch (c) {
    case 'device_rate_limit':
      return 'Device rate-limited';
    case 'device_integrity':
      return 'Device integrity';
    case 'liveness_spoofing':
      return 'Liveness / spoofing';
    case 'biometric_mismatch':
      return 'Biometric mismatch';
    case 'document_invalid':
      return 'Document invalid';
    case 'other':
      return 'Other';
  }
}
